import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

export function SkontoCalculatorModal({ open, onClose, terms = [] }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ termId: '', baseAmount: '1000', invoiceDate: '', paymentDate: '' });
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm({ termId: terms[0]?.id || '', baseAmount: '1000', invoiceDate: '', paymentDate: '' }); setError(null); } }, [open, terms]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const calc = useMutation({
    mutationFn: () => financeApi.computePaymentTerm({ termId: form.termId, baseAmount: Number(form.baseAmount), invoiceDate: form.invoiceDate, paymentDate: form.paymentDate }).then((r) => r.items),
    onError: (e) => setError(e?.message || t('common.loadError')),
    onSuccess: () => setError(null),
  });
  const result = calc.data;

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.termId || !form.invoiceDate || !form.paymentDate || !form.baseAmount) { setError(t('pt.error.calcRequired')); return; }
    calc.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title={t('pt.calc.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
        <Button variant="primary" onClick={submit} disabled={calc.isPending}>{t('pt.action.calc')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field"><span className="field-label">{t('pt.calc.term')}</span>
            <select className="input" value={form.termId} onChange={set('termId')} required>
              <option value="">-</option>
              {terms.map((tt) => <option key={tt.id} value={tt.id}>{tt.code} · {tt.name}</option>)}
            </select>
          </label>
          <label className="field" style={{ maxWidth: 160 }}><span className="field-label">{t('pt.calc.base')}</span>
            <input className="input num" type="number" min="0" step="0.01" value={form.baseAmount} onChange={set('baseAmount')} required />
          </label>
        </div>
        <div className="field-row">
          <label className="field"><span className="field-label">{t('pt.calc.invoiceDate')}</span>
            <input className="input" type="date" value={form.invoiceDate} onChange={set('invoiceDate')} required />
          </label>
          <label className="field"><span className="field-label">{t('pt.calc.paymentDate')}</span>
            <input className="input" type="date" value={form.paymentDate} onChange={set('paymentDate')} required />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
        {result && (
          <div className="card" style={{ padding: 'var(--space-3)' }}>
            <div className="form-stack" style={{ gap: 'var(--space-1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('pt.calc.due')}</span><span className="mono">{formatDate(result.dueDate)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('pt.calc.deadline')}</span><span className="mono">{formatDate(result.discountDeadline)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="muted">{t('pt.calc.discount')}</span><span className="mono num">{formatCurrency(result.discountAmount)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span><strong>{t('pt.calc.payable')}</strong></span><span className="mono num"><strong>{formatCurrency(result.payable)}</strong></span></div>
              <div style={{ marginTop: 'var(--space-2)' }}>
                {result.withinDiscount ? <Badge variant="success">{t('pt.calc.within')}</Badge> : <Badge variant="neutral">{t('pt.calc.outside')}</Badge>}
              </div>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
